import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { SendHorizontalIcon, Loader2Icon, SquareIcon, ShrinkIcon } from 'lucide-react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { MessageRow } from './MessageRow';
import { ThinkingIndicator } from './ThinkingIndicator';
import { ToolCallHandlersContext, ChartAutoRunContext } from '../assistant-ui/chart-tool-call';
import type { ToolCallHandlers } from '../assistant-ui/chart-tool-call';
import { cn } from '@/lib/utils';

interface ThreadProps {
  runtime: any; // The runtime from useCustomLangGraphRuntime
  initialQuestion?: string;
  toolCallHandlers?: ToolCallHandlers;
  autoRunCharts?: boolean;
  emptyStateMessage?: string;
  onValidationComplete?: () => void;
  onCompact?: () => Promise<any>;
  messageCount?: number;
  // When true, a brand-new conversation renders the input centered in the page;
  // when false (default) the input stays docked at the bottom as soon as the
  // thread mounts. Set per-tenant (Settings → Ellie) or per embed session.
  centerInputOnNewChat?: boolean;
  // Placeholder text for the message input.
  placeholder?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: Array<{ type: 'text'; text: string } | { type: 'tool-call'; toolCallId: string; toolName: string; args: any; argsText: string; result?: string }>;
  status?: { type: 'incomplete'; reason: 'error'; error: Error };
}

type VirtualItem =
  | { type: 'empty'; key: string }
  | { type: 'message'; key: string; message: Message }
  | { type: 'loading'; key: string };


export const Thread: React.FC<ThreadProps> = ({
  runtime,
  initialQuestion,
  toolCallHandlers,
  autoRunCharts = true,
  emptyStateMessage = '',
  onValidationComplete,
  onCompact,
  messageCount,
  centerInputOnNewChat = false,
  placeholder = 'Write a message...',
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isRunningRef = useRef(false);
  const initialQuestionSentRef = useRef(false);
  const [showCompactDialog, setShowCompactDialog] = useState(false);
  const [isCompacting, setIsCompacting] = useState(false);

  // Keep ref in sync so atBottomStateChange can read without stale closure
  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);

  // Subscribe to runtime thread state
  useEffect(() => {
    const unsubscribe = runtime.thread.subscribe(() => {
      try {
        const state = runtime.thread.getState();
        if (!state) {
          return;
        }

        setIsRunning(state.isRunning || false);

        if (state.messages) {
          setMessages(state.messages as Message[]);
        }
      } catch (err) {
        console.debug('Thread state not ready:', err);
      }
    });

    // Get initial state
    try {
      const state = runtime.thread.getState();
      if (state) {
        setIsRunning(state.isRunning || false);
        if (state.messages) {
          setMessages(state.messages as Message[]);
        }
      }
    } catch (err) {
      console.debug('Initial thread state not ready:', err);
    }

    return () => unsubscribe?.();
  }, [runtime]);

  // Handle initial question — send only once per mount
  useEffect(() => {
    if (initialQuestion && !initialQuestionSentRef.current) {
      initialQuestionSentRef.current = true;
      runtime.thread.append({
        role: 'user',
        content: [{ type: 'text', text: initialQuestion }],
      });
    }
  }, [runtime, initialQuestion]);

  // Programmatic message sending (used by tool call components like file upload)
  const sendMessage = useCallback((text: string) => {
    if (!text.trim()) return;
    setShouldAutoScroll(true);
    runtime.thread.append({
      role: 'user',
      content: [{ type: 'text', text }],
    });
  }, [runtime]);

  // Merge onSendMessage into tool call handlers
  const mergedToolCallHandlers = useMemo<ToolCallHandlers | undefined>(() => {
    return {
      ...toolCallHandlers,
      onSendMessage: sendMessage,
    };
  }, [toolCallHandlers, sendMessage]);

  // Handle sending a message
  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text || isRunning) {
      return;
    }

    // Re-enable auto-scroll when user sends a new message
    setShouldAutoScroll(true);

    runtime.thread.append({
      role: 'user',
      content: [{ type: 'text', text }],
    });

    setInputValue('');
    textareaRef.current?.focus();
  }, [inputValue, isRunning, runtime]);

  // Handle stopping a running stream
  const handleStop = useCallback(() => {
    try {
      runtime.thread.cancelRun();
    } catch (err) {
      console.warn('Failed to cancel run:', err);
    }
  }, [runtime]);

  // Compact the conversation: close confirmation, show blocking loader, run
  const handleCompact = useCallback(async () => {
    if (!onCompact) return;
    setShowCompactDialog(false);
    setIsCompacting(true);
    try {
      await onCompact();
    } catch (err) {
      console.error('Failed to compact conversation:', err);
    } finally {
      setIsCompacting(false);
    }
  }, [onCompact]);

  // Handle Enter key (Shift+Enter for new line)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [inputValue]);

  // Focus the input on a brand-new chat so the user can start typing right
  // away. Mount-only (Thread remounts per thread) so we don't steal focus when
  // returning to a conversation that already has messages.
  useEffect(() => {
    if (messages.length === 0 && !isRunning) {
      textareaRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Return focus to the input when a run finishes so the user can type a
  // follow-up without clicking back in. The textarea is disabled (and thus
  // blurred) while streaming, and on the first message it remounts when the
  // layout switches from centered to bottom — both drop focus.
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (wasRunningRef.current && !isRunning) {
      textareaRef.current?.focus();
    }
    wasRunningRef.current = isRunning;
  }, [isRunning]);

  // Detect completed validation status tool call
  const validationComplete = useMemo(() => {
    if (isRunning) return false;
    return messages.some((msg) =>
      msg.content.some(
        (part) =>
          part.type === 'tool-call' &&
          (part.toolName === 'complete_use_case_discovery' || part.toolName === 'complete_data_source_validation' || part.toolName === 'complete_data_modeling') &&
          part.result !== undefined
      )
    );
  }, [messages, isRunning]);

  const validationNotifiedRef = useRef(false);
  useEffect(() => {
    if (validationComplete && !validationNotifiedRef.current) {
      validationNotifiedRef.current = true;
      onValidationComplete?.();
    }
  }, [validationComplete, onValidationComplete]);

  // Build virtual items list
  const items = useMemo<VirtualItem[]>(() => {
    const result: VirtualItem[] = [];

    if (messages.length === 0) {
      result.push({ type: 'empty', key: 'empty-state' });
    }

    for (const msg of messages) {
      result.push({ type: 'message', key: msg.id, message: msg });
    }

    if (isRunning) {
      result.push({ type: 'loading', key: 'loading-indicator' });
    }

    return result;
  }, [messages, isRunning]);

  // Virtuoso callbacks
  const handleFollowOutput = useCallback(() => {
    return shouldAutoScroll ? 'smooth' as const : false as const;
  }, [shouldAutoScroll]);

  const handleAtBottomStateChange = useCallback((atBottom: boolean) => {
    if (atBottom) {
      setShouldAutoScroll(true);
    } else if (!isRunningRef.current) {
      // Only disable if not actively streaming — smooth scroll briefly reports not-at-bottom
      setShouldAutoScroll(false);
    }
  }, []);

  const renderItem = useCallback((_index: number, item: VirtualItem) => {
    if (item.type === 'empty') {
      return null;
    }

    if (item.type === 'loading') {
      return (
        <div className="flex justify-start mb-4 px-16">
          <div className="max-w-[80%]">
            <ThinkingIndicator />
          </div>
        </div>
      );
    }

    return (
      <div className="px-16 py-2">
        <MessageRow message={item.message} />
      </div>
    );
  }, [emptyStateMessage]);

  // A brand-new conversation has no messages yet — center the input so the
  // attention focuses on the text box instead of a tall blank thread. Tenants
  // (and embed sessions) can opt out, keeping the input docked at the bottom.
  const isEmpty = messages.length === 0 && centerInputOnNewChat;

  const inputControls = (
    <div className="flex items-end gap-2">
      <textarea
        ref={textareaRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={isRunning}
        rows={1}
        className={cn(
          "flex min-h-[2.5rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 resize-none overflow-hidden"
        )}
      />
      {onCompact && (messageCount ?? messages.length) > 10 && !isRunning && (
        <Button
          onClick={() => setShowCompactDialog(true)}
          size="icon"
          variant="ghost"
          className="h-10 w-10 shrink-0"
          title="Compact conversation"
        >
          <ShrinkIcon className="h-4 w-4" />
        </Button>
      )}
      {isRunning ? (
        <Button
          onClick={handleStop}
          size="icon"
          variant="destructive"
          className="h-10 w-10 shrink-0"
        >
          <SquareIcon className="h-4 w-4" />
        </Button>
      ) : (
        <Button
          onClick={handleSend}
          disabled={!inputValue.trim()}
          size="icon"
          className="h-10 w-10 shrink-0"
        >
          <SendHorizontalIcon className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  return (
    <ToolCallHandlersContext.Provider value={mergedToolCallHandlers}>
      <ChartAutoRunContext.Provider value={autoRunCharts}>
        <div className="h-full flex flex-col">
          {isEmpty ? (
            <div className="flex-1 flex flex-col items-center justify-center px-4">
              <div className="w-full max-w-2xl">
                {emptyStateMessage && (
                  <p className="mb-4 text-center text-2xl font-semibold text-foreground">
                    {emptyStateMessage}
                  </p>
                )}
                {inputControls}
              </div>
            </div>
          ) : (
            <>
              <Virtuoso
                ref={virtuosoRef}
                className="flex-1"
                data={items}
                computeItemKey={(_index, item) => item.key}
                initialTopMostItemIndex={Math.max(0, items.length - 1)}
                followOutput={handleFollowOutput}
                atBottomStateChange={handleAtBottomStateChange}
                atBottomThreshold={100}
                increaseViewportBy={{ top: 400, bottom: 400 }}
                itemContent={renderItem}
                components={{
                  Header: () => <div className="h-12" />,
                  Footer: () => <div className="h-4" />,
                }}
              />

              <div className="border-t p-4">{inputControls}</div>
            </>
          )}
        </div>

        {/* Confirmation dialog */}
        <AlertDialog open={showCompactDialog} onOpenChange={setShowCompactDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Compact Conversation</AlertDialogTitle>
              <AlertDialogDescription>
                This will summarize the earlier part of your conversation to reduce token usage.
                Tool call details and intermediate messages will be collapsed into a concise summary.
                The last few messages will be preserved as-is. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleCompact}>
                Compact
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Blocking loader while compacting */}
        <AlertDialog open={isCompacting}>
          <AlertDialogContent className="max-w-sm" onEscapeKeyDown={(e) => e.preventDefault()}>
            <div className="flex flex-col items-center gap-4 py-4">
              <Loader2Icon className="h-8 w-8 animate-spin text-primary" />
              <AlertDialogTitle>Compacting conversation...</AlertDialogTitle>
              <AlertDialogDescription className="text-center">
                Summarizing earlier messages. This may take a moment.
              </AlertDialogDescription>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </ChartAutoRunContext.Provider>
    </ToolCallHandlersContext.Provider>
  );
};
